import { Button, FieldMessage, Flex, Icon } from '@servicetitan/anvil2';
import Launch from '@servicetitan/anvil2/assets/icons/material/round/launch.svg';
import { SetupDataStore, useProviderConfig } from '@servicetitan/financing-workflow-shared';
import { DeliveryMethod, LoanApplicationSubmitSource2 } from '@servicetitan/financing-api-client';
import { FinancedAmountStore } from '../financing-information/financedAmountStore';
import { useAffirmPrequalification } from './useAffirmPrequalification';

interface SelectPlanButtonProps {
    onClick: (isSelected: boolean) => void;
    planNumber: string;
    /**
     * Provider that answered this calculation (from the calculation response). Drives the CTA so
     * that a plan whose resolved provider is Affirm renders the direct pre-qualification button even
     * when the setup/BU provider differs (e.g. a range-gate/waterfall that returns Affirm plans
     * under a Service Finance setup). Falls back to the setup provider when absent.
     */
    providerName?: string | null;
    disabled?: boolean;
}

export const SelectPlanButton = ({
    onClick,
    providerName,
    disabled = false,
}: SelectPlanButtonProps) => {
    const providerConfig = useProviderConfig(providerName ?? undefined);
    const { setupData } = SetupDataStore.useStore();
    const { financedAmount } = FinancedAmountStore.useStore();
    const affirmPrequalification = useAffirmPrequalification();

    const isDirectPrequal = providerConfig.directPrequalification === true;
    const isExternalPrequalEnabled = providerConfig.enableExternalPrequalification;

    const buttonText = isDirectPrequal
        ? 'Pre-qualify with Affirm'
        : isExternalPrequalEnabled
          ? 'Start Pre-Qualification'
          : 'Select Plan';

    // Direct prequalification (Affirm) submits an external prequalification and opens the returned
    // provider-hosted URL, fully bypassing the in-app application form. The submit is not
    // plan-specific, so no planNumber is sent and the SelectedPlan/onPlanSelected flow is skipped.
    const handleDirectPrequal = () => {
        if (affirmPrequalification.isPending) {
            return;
        }

        affirmPrequalification.mutate({
            providerName: providerName ?? setupData?.provider,
            tenantId: setupData?.tenantId,
            buId: setupData?.buId,
            amount: financedAmount,
            jobId: setupData?.jobId,
            technicianId: setupData?.technicianId,
            customerId: setupData?.customerId,
            source:
                setupData?.source ??
                (setupData?.role === 'technician'
                    ? LoanApplicationSubmitSource2.Mobile
                    : LoanApplicationSubmitSource2.OnlineEstimates),
            applicantDeliveryMethod: DeliveryMethod.None,
            isPrequalification: false,
            sendEmail: false,
        });
    };

    const handleClick = () => {
        if (isDirectPrequal) {
            handleDirectPrequal();
            return;
        }
        onClick(true);
    };

    const isDisabled = disabled || (isDirectPrequal && affirmPrequalification.isPending);
    const showLaunchIcon = isExternalPrequalEnabled || isDirectPrequal;

    return (
        <Flex direction="column" gap="2" style={{ width: '100%' }}>
            <Button
                onClick={handleClick}
                appearance="primary"
                style={{ width: '100%' }}
                disabled={isDisabled}
            >
                {buttonText}
                {showLaunchIcon && <Icon inherit aria-hidden svg={Launch} />}
            </Button>
            {isDirectPrequal && affirmPrequalification.isError && (
                <FieldMessage error="Something went wrong while starting your Affirm application. Please try again." />
            )}
        </Flex>
    );
};
